import {
    createContext,
    ReactElement,
    ReactNode,
    Suspense,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { routes } from "./Router";
import { matchPath, useLocation } from "react-router-dom";
import { useLoggedInUser } from "@/user/LoggedInUserProvider";
import { ErrorBoundary } from "@/error/ErrorBoundary";
import { PreloadCompatibleRouteContext } from "./PreloadCompatibleRouteProvider";
import omit from "lodash/omit";
import map from "lodash/map";
import { useAuth } from "react-oidc-context";

export interface PreloadElement {
    key: string;
    element: ReactElement;
}

interface PreloadContextType {
    setPreloadRoute: (path: string) => void;
    setPreloadElement: (preloadElement: PreloadElement) => void;
    isProviderActive: boolean;
    isPreloading: boolean;
}

interface PreloadState {
    element: ReactElement;
    params: Record<string, string | undefined>;
}

const CLEAR_TIMER = 3_000;
const RECENCY_TIMER = 20_000;

const PreloadContext = createContext<PreloadContextType>({
    setPreloadRoute: () => undefined,
    setPreloadElement: () => undefined,
    isProviderActive: false,
    isPreloading: false
});

export const usePreload = () => {
    return useContext(PreloadContext);
};

export const PreloadProvider = ({ children }: { children: ReactNode }) => {
    const { isSuper } = useLoggedInUser();
    const [state, setState] = useState<Record<string, PreloadState>>({});

    const historyRef = useRef<Record<string, number>>({});
    const checkAndSetRecency = (key: string) => {
        if (historyRef.current[key]) {
            const threshold = Date.now() - RECENCY_TIMER;
            if (historyRef.current[key] > threshold) {
                // preload already happened recently
                return false;
            }
        }
        historyRef.current[key] = Date.now();
        return true;
    };

    const removeKey = (key: string) => {
        setState(prevState => omit(prevState, key));
    };

    const { pathname } = useLocation();
    useEffect(() => {
        // clearing preload after page change
        removeKey(`route:${pathname}`);
    }, [pathname]);

    const setPreloadRoute = (path: string) => {
        const key = `route:${path}`;
        if (!checkAndSetRecency(`route:${path}`)) {
            return;
        }

        // Preload the path
        routes.forEach(route => {
            if (route.allowAnyone || isSuper) {
                const match = matchPath(
                    {
                        path: route.path
                    },
                    path
                );
                if (match) {
                    // preloading path
                    setState(x => ({
                        ...x,
                        [key]: { element: route.element, params: match.params }
                    }));
                }
            }
        });
    };

    const setPreloadElement = (value: PreloadElement) => {
        const key = `element:${value.key}`;
        if (!checkAndSetRecency(key)) {
            return;
        }

        setState(x => ({
            ...x,
            [key]: { element: value.element, params: {} }
        }));
    };

    return (
        <PreloadContext.Provider
            value={{
                setPreloadRoute,
                setPreloadElement,
                isProviderActive: true,
                isPreloading: false
            }}
        >
            {children}
            {map(state, (value, key) => (
                <Preload
                    key={key}
                    element={value.element}
                    params={value.params}
                    preloadKey={key}
                    removeKey={removeKey}
                />
            ))}
        </PreloadContext.Provider>
    );
};

const Preload = ({
    element,
    params,
    preloadKey,
    removeKey
}: PreloadState & { preloadKey: string; removeKey: (key: string) => void }) => {
    const clearTimerRef = useRef<number | undefined>();
    useEffect(() => {
        if (clearTimerRef.current !== undefined) {
            window.clearTimeout(clearTimerRef.current);
        }
        clearTimerRef.current = window.setTimeout(() => {
            // clearing preload after timer
            removeKey(preloadKey);
            clearTimerRef.current = undefined;
        }, CLEAR_TIMER);
    }, [preloadKey, removeKey]);

    const { isAuthenticated } = useAuth();
    if (!isAuthenticated) {
        return undefined;
    }

    return (
        <div style={{ display: "none", contentVisibility: "hidden" }}>
            <PreloadContext.Provider
                value={{
                    setPreloadRoute: () => undefined,
                    setPreloadElement: () => undefined,
                    isProviderActive: true,
                    isPreloading: true
                }}
            >
                <PreloadCompatibleRouteContext.Provider value={{ params }}>
                    <ErrorBoundary>
                        <Suspense>{element}</Suspense>
                    </ErrorBoundary>
                </PreloadCompatibleRouteContext.Provider>
            </PreloadContext.Provider>
        </div>
    );
};
